import React, { useState } from 'react';
import { Copy, CheckCircle, ExternalLink, AlertTriangle } from 'lucide-react';

const SchemaPreview = ({ schema, schemaType, validationStatus }) => {
  const [copied, setCopied] = useState(false);

  const copyToClipboard = async () => {
    if (!schema) return;

    try {
      await navigator.clipboard?.writeText(JSON.stringify(schema, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const testInGoogle = () => {
    if (!schema) return;
    
    // Encode schema as data URL for testing
    const schemaStr = JSON.stringify(schema, null, 2);
    const dataUrl = `data:text/html,<script type="application/ld+json">${encodeURIComponent(schemaStr)}</script>`;
    window.open(`https://search.google.com/test/rich-results?url=${encodeURIComponent(dataUrl)}`, '_blank');
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6 sticky top-6">
      <h2 className="text-xl font-bold text-gray-900 mb-4">Schema Preview</h2>
      {!schema ? (
        <div className="text-center py-12">
          <AlertTriangle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-600">
            Fill out the form and click "Generate Schema" to preview
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Validation Status */}
          {validationStatus && (
            <div className={`p-4 rounded-lg flex items-center gap-3 ${
              validationStatus === 'valid' ?'bg-green-50 border border-green-200' :'bg-yellow-50 border border-yellow-200'
            }`}>
              <CheckCircle className={`w-5 h-5 ${
                validationStatus === 'valid' ? 'text-green-600' : 'text-yellow-600'
              }`} />
              <div>
                <p className={`font-medium ${
                  validationStatus === 'valid' ? 'text-green-800' : 'text-yellow-800'
                }`}>
                  Schema {validationStatus === 'valid' ? 'Valid' : 'Generated'}
                </p>
                <p className={`text-sm ${
                  validationStatus === 'valid' ? 'text-green-700' : 'text-yellow-700'
                }`}>
                  {schemaType} schema ready for implementation
                </p>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-2">
            <button
              onClick={copyToClipboard}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              {copied ? (
                <>
                  <CheckCircle className="w-4 h-4" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" />
                  Copy
                </>
              )}
            </button>

            <button
              onClick={testInGoogle}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <ExternalLink className="w-4 h-4" />
              Test
            </button>
          </div>

          {/* Schema JSON */}
          <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
            <pre className="text-sm text-green-400">
              <code>{JSON.stringify(schema, null, 2)}</code>
            </pre>
          </div>

          {/* Implementation Instructions */}
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h3 className="font-semibold text-blue-900 mb-2">Implementation</h3>
            <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
              <li>Copy the schema JSON above</li>
              <li>Add to your page's &lt;head&gt; section</li>
              <li>Wrap in &lt;script type="application/ld+json"&gt;</li>
              <li>Test using Google Rich Results Test</li>
              <li>Verify in Google Search Console</li>
            </ol>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 gap-4">
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-600 mb-1">Schema Type</p>
              <p className="font-semibold text-gray-900">{schemaType}</p>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-600 mb-1">Size</p>
              <p className="font-semibold text-gray-900">
                {(JSON.stringify(schema)?.length / 1024)?.toFixed(2)} KB
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SchemaPreview;